import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import { SR_API_ACTION_UTILITY_PARTNER_UPDATE_EXPERIENCE_CONFIGURATION } from 'api/utilityActionTypes';

import { UTILITY_PARTNER_CUSTOMER_TYPE_PROPERTY_MANAGER } from 'common/utility/constants';

import { createInitialValues } from 'client/helpers/formikHelpers';

import SrCard, {
  SR_CARD_BULLET_COLOR_BLUE,
  SR_CARD_CONTENT_TYPE_FORM_GRID,
} from 'client/components/SrCard/SrCard';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

import requestUtilityApi from 'client/actions/requestUtilityApi';
import UtilityPartnerActions from 'client/actions/UtilityPartner';

import SrBox from 'client/components/SrBox';
import SrLabel from 'client/components/SrLabel/SrLabel';
import SrButton from 'client/components/SrButton/SrButton';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';

const ANNOUNCEMENT_OPTIONS = [
  { label: 'Enabled', value: 'enabled' },
  { label: 'Disabled', value: 'disabled' },
];

const getFormFields = ({ isEditable }) => {
  return [
    {
      id: 'tenantLiabilityBanner',
      title: 'Tenant Liability Banner',
      type: 'select',
      properties: {
        placeholder: 'Select status',
        choices: ANNOUNCEMENT_OPTIONS,
        disabled: !isEditable,
      },
      validations: ['required'],
    },
  ];
};

class TenantLiabilityBannerCard extends Component {
  static propTypes = {
    utilityPartner: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.state = {
      errorMessage: null,
    };
  }

  _onSubmitForm = (formValues, formikBag) => {
    const { utilityPartner, dispatch, onSuccess } = this.props;

    this.setState({ errorMessage: null });

    const tenantLiabilityBanner = formValues.tenantLiabilityBanner === 'enabled';

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PARTNER_UPDATE_EXPERIENCE_CONFIGURATION,
      payload: {
        utilityPartnerId: utilityPartner.id,
        configuration: {
          tenantLiabilityBanner,
        },
      },
      onError: (error) => {
        this.setState({
          errorMessage: error.message,
        });
        formikBag.setSubmitting(false);
      },
      onSuccess: () => {
        dispatch(UtilityPartnerActions.getBySlug, {
          slug: utilityPartner.slug,
          forceRefresh: true,
        })
          .then(() => {
            if (onSuccess) {
              onSuccess();
            }
          })
          .catch((error) => {
            this.setState({
              errorMessage: error.message || 'Failed to refresh partner data',
            });
          })
          .finally(() => {
            formikBag.setSubmitting(false);
          });
      },
    });
  };

  render() {
    const { utilityPartner } = this.props;
    const { errorMessage } = this.state;

    const isPropertyManager =
      utilityPartner.customerType === UTILITY_PARTNER_CUSTOMER_TYPE_PROPERTY_MANAGER;

    const currentValue = utilityPartner.experienceConfiguration?.tenantLiabilityBanner || false;

    const formFields = getFormFields({ isEditable: isPropertyManager });

    return (
      <Formik
        enableReinitialize
        onSubmit={this._onSubmitForm}
        initialValues={createInitialValues({
          formFields,
          defaultValues: {
            tenantLiabilityBanner: currentValue ? 'enabled' : 'disabled',
          },
        })}
        render={(formikBag) => {
          return (
            <Form>
              <SrCard
                color={SR_CARD_BULLET_COLOR_BLUE}
                contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                headerFlexDirection="column"
                header={
                  <SrBox display="flex" alignItems="center" columnGap="12px">
                    <span>Tenant Liability Banner</span>
                    <SrLabel
                      borderColor="#152c5b"
                      borderWidth="1"
                      color="blueNavy"
                      fontSize="small"
                      fontWeight="500"
                      padding="4px 12px"
                      label="UP Admin"
                    />
                  </SrBox>
                }
                subheader="Enable or disable the tenant liability banner for this partner."
              >
                {!isPropertyManager && (
                  <SrBox
                    backgroundColor="#FFF3CD"
                    borderRadius="8px"
                    padding="12px 16px"
                    marginBottom="16px"
                    display="flex"
                    alignItems="center"
                    columnGap="12px"
                  >
                    <img
                      src="/images/information-icon-yellow.svg"
                      alt="info"
                      width="20"
                      height="20"
                    />
                    <span style={{ color: '#856404', fontSize: '14px' }}>
                      This setting is only available for Property Manager partners.
                    </span>
                  </SrBox>
                )}
                <SrBox
                  display="flex"
                  alignItems="flex-end"
                  justifyContent="space-between"
                  columnGap="16px"
                >
                  <SrBox flex="1 0 auto" maxWidth={300}>
                    {formFields.map((formField) => (
                      <SrFormikField
                        key={formField.id}
                        formField={formField}
                        formikBag={formikBag}
                        minWidth={300}
                        theme={SR_FIELD_THEME_ADOBE}
                      />
                    ))}
                  </SrBox>
                  {isPropertyManager && (
                    <SrBox>
                      <SrButton type="submit" disabled={formikBag.isSubmitting}>
                        Save
                      </SrButton>
                    </SrBox>
                  )}
                </SrBox>

                {errorMessage && (
                  <SrBox marginTop="20px">
                    <SrErrorMessage>{errorMessage}</SrErrorMessage>
                  </SrBox>
                )}
              </SrCard>
            </Form>
          );
        }}
      />
    );
  }
}

TenantLiabilityBannerCard.displayName = 'TenantLiabilityBannerCard';

export default TenantLiabilityBannerCard;
